import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';

/**
 * Cross-field validator for Tax_ID.
 * Reads the sibling US_or_CA control from the parent FormGroup to decide which format to enforce.
 *
 * Returns a rich ValidationErrors object so the pre-submit review panel can display
 * the correct message and offer a one-click Fix without any field-specific code in the components.
 *
 * Error shape:  { taxIdFormat: { label, message, suggestedFix, canAutoFix } }
 *
 * To add future fixable validations, create a similar validator file and attach it
 * to the relevant form control — the review panel picks it up automatically.
 */
export function taxIdValidator(): ValidatorFn {
  return (ctrl: AbstractControl): ValidationErrors | null => {
    const value = (ctrl.value as string) ?? '';
    if (!value) return null; // required validator handles empty fields

    const country = (ctrl.parent?.get('US_or_CA')?.value as string) ?? '';
    const digits = value.replace(/\D/g, '');

    if (country === 'US') {
      const isEIN = /^\d{2}-\d{7}$/.test(value);   // EIN:  XX-XXXXXXX
      if (isEIN) return null;
      // Auto-fix: 9 raw digits — suggest EIN format
      const fix = digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : null;
      return {
        taxIdFormat: {
          label: 'F.E.I.N.',
          message: 'US Tax ID must be EIN format (XX-XXXXXXX)',
          suggestedFix: fix,
          canAutoFix: !!fix,
        },
      };
    }

    if (country === 'Canada') {
      if (/^\d{9}$/.test(value)) return null; // already valid
      const fix = digits.length === 9 ? digits : null;
      return {
        taxIdFormat: {
          label: 'F.E.I.N. Tax ID',
          message: 'Canadian Tax ID must be exactly 9 digits',
          suggestedFix: fix,
          canAutoFix: !!fix,
        },
      };
    }

    return null; // country not yet selected — skip
  };
}
